import { ReactNode, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAppContext from '@/hooks/useAppContext';
import { EUserRole } from '@/constants';

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles: EUserRole[];
  redirectPath?: string;
}

function ProtectedRoute({
  children,
  allowedRoles,
  redirectPath = '/doctor-conversations',
}: ProtectedRouteProps) {
  const { userRole, isAppLoading } = useAppContext();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const chatId = params.get('chatId');

  useEffect(() => {
    if (!isAppLoading && userRole && !allowedRoles.includes(userRole)) {
      // If user role is not allowed, redirect to appropriate path
      if (userRole === EUserRole.DOCTOR) {
        navigate(redirectPath, { replace: true });
      }
    } else if (
      /**
       * Redirect logic for users with USER role trying to access a specific chat
       * If they have a chatId in the URL, redirect them to that conversation
       * Otherwise, redirect to the default path (e.g., home or conversations list)
       */
      userRole === EUserRole.USER &&
      chatId
    ) {
      const redirectTo = `/conversation/${chatId}`;
      navigate(redirectTo, { replace: true });
    }
  }, [userRole, isAppLoading, allowedRoles, navigate, redirectPath, chatId]);

  // Show loading state while determining user role
  if (isAppLoading || !userRole) {
    return null; // or a loading spinner
  }

  // If user role is not allowed, don't render children
  if (!allowedRoles.includes(userRole)) {
    return null;
  }

  return <>{children}</>;
}

export default ProtectedRoute;
